import JumboCardQuick from '@jumbo/components/JumboCardQuick';
import GroupsIcon from '@mui/icons-material/Groups';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import { Box, Chip, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import {
    calculateCounts,
    canViewSecretFiles,
    chunkData,
    getGroupedListForDepartment,
    getVoteStatus,
    groupAgendasByDepartment,
    groupDecisionsBySubject,
    sortByProperty,
    getLocaleDateTime,
    getUserFullName
} from 'app/utils/appHelpers';
import { greenColor, primaryColor, redColor } from 'config';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTemplateFontSettings } from 'styles/templateFontSettings';
import ReviewFileListTable from '../Table/ReviewFileListTable';

function PreviewMeetingCard({ meeting, meetingUsers = [], decisions = [], votes = [], reviews = [], decisionFiles = [], usersSectionId }) {
    const [groupedList, setGroupedList] = useState([]); // YK için departman bazında; diğerinde subjectGroups dizisi
    const [decisionCount, setDecisionCount] = useState(0);
    const subjectList = useSelector((state) => state.subject.data);
    const userList = useSelector((state) => state.user.data);
    const fileGroup = 6;
    
    const [ready, setReady] = useState(false);
    const { authUser } = useSelector(state => state.auth);

    useEffect(() => {
        const buildPreview = async () => {
            if (!meeting?.id) return;
            try {
                setReady(false);

                let members = (meetingUsers || []).filter(item => item.type === 1);
                members = sortByProperty(members, "indexNo");

                const decisionList = sortByProperty([...(decisions || [])], "decisionNo");
                const voteList = votes || [];
                const reviewsData = reviews || [];
                const files = decisionFiles || [];

                const processedDecisions = await processDecisions(voteList, members, decisionList, meeting);

                const decisionsWithFiles = processedDecisions.map((decision) => {
                    const data = files.filter(item => item.decisionId === decision.id);

                    let agendaFiles = data.filter(item => item.fileInfo === null || item.fileInfo === "");
                    agendaFiles.forEach(element => {
                        const user = userList.find(item => item.id === element.adminUserId);
                        element.user = user;
                    });

                    const links = data.filter(item => item.fileInfo !== "" && item.ext === "");
                    const groupedData = chunkData(agendaFiles, fileGroup);
                    const groupedDataLink = chunkData(links, fileGroup);
                    const decisionReviews = reviewsData.filter(review => review.decisionId === decision.id);

                    return { ...decision, groupedData, groupedDataLink, reviews: decisionReviews };
                });

                setDecisionCount(decisionsWithFiles.length);

                const normal = decisionsWithFiles.filter(d => d.supp !== 'Evet');
                const extra = decisionsWithFiles.filter(d => d.supp === 'Evet');

                if (meeting.inst === "YK") {
                    const sortedList = [];

                    const normalDeptGroups = groupAgendasByDepartment(normal);
                    for (const [deptKey, deptDecisions] of normalDeptGroups) {
                        const { acceptCount, rejectCount, sameCount } = calculateCounts(deptDecisions);
                        const counts = [];
                        if (acceptCount > 0) counts.push(`Kabul: ${acceptCount}`);
                        if (rejectCount > 0) counts.push(`Ret: ${rejectCount}`);
                        if (sameCount > 0) counts.push(`Eşit: ${sameCount}`);
                        const countText = counts.length > 0 ? ` (${counts.join(", ")})` : "";

                        const subjectGroups = await getGroupedListForDepartment(deptDecisions, subjectList);
                        const subjects = subjectGroups.map((group) => {
                            const subCounts = [];
                            if (group.acceptCount > 0) subCounts.push(`Kabul: ${group.acceptCount}`);
                            if (group.rejectCount > 0) subCounts.push(`Ret: ${group.rejectCount}`);
                            if (group.sameCount > 0) subCounts.push(`Eşit: ${group.sameCount}`);
                            const subCountText = subCounts.length > 0 ? ` (${subCounts.join(", ")})` : "";
                            return {
                                subjectName: `${group.subjectName}${subCountText}`,
                                items: group.items,
                            };
                        });
                        sortedList.push({
                            department: `${deptKey}${countText}`,
                            subjects: subjects,
                        });
                    }

                    if (extra.length > 0) {
                        sortedList.push({ department: 'EK GÜNDEMLER', subjects: [] });

                        const extraDeptGroups = groupAgendasByDepartment(extra);
                        for (const [deptKey, deptDecisions] of extraDeptGroups) {
                            const { acceptCount, rejectCount, sameCount } = calculateCounts(deptDecisions);
                            const counts = [];
                            if (acceptCount > 0) counts.push(`Kabul: ${acceptCount}`);
                            if (rejectCount > 0) counts.push(`Ret: ${rejectCount}`);
                            if (sameCount > 0) counts.push(`Eşit: ${sameCount}`);
                            const countText = counts.length > 0 ? ` (${counts.join(", ")})` : "";

                            const subjectGroups = await getGroupedListForDepartment(deptDecisions, subjectList);
                            const subjects = subjectGroups.map((group) => {
                                const subCounts = [];
                                if (group.acceptCount > 0) subCounts.push(`Kabul: ${group.acceptCount}`);
                                if (group.rejectCount > 0) subCounts.push(`Ret: ${group.rejectCount}`);
                                if (group.sameCount > 0) subCounts.push(`Eşit: ${group.sameCount}`);
                                const subCountText = subCounts.length > 0 ? ` (${subCounts.join(", ")})` : "";
                                return {
                                    subjectName: `${group.subjectName}${subCountText}`,
                                    items: group.items,
                                };
                            });
                            sortedList.push({
                                department: `${deptKey}${countText}`,
                                subjects: subjects,
                            });
                        }
                    }

                    setGroupedList(sortedList);
                } else {
                    const sortedList = [];

                    const normalGroupedDecisions = groupDecisionsBySubject(normal);
                    const normalSubjectsWithCounts = normalGroupedDecisions.map(([subjectId, agendas]) => {
                        const subject = subjectList.find((sub) => sub.id === Number(subjectId));
                        const subjectName = subject ? subject.name : "Tanımsız Konu";
                        const { acceptCount, rejectCount, sameCount } = calculateCounts(agendas);
                        const counts = [];
                        if (acceptCount > 0) counts.push(`Kabul: ${acceptCount}`);
                        if (rejectCount > 0) counts.push(`Ret: ${rejectCount}`);
                        if (sameCount > 0) counts.push(`Eşit: ${sameCount}`);
                        const countText = counts.length > 0 ? ` (${counts.join(", ")})` : "";
                        return {
                            subjectId,
                            subjectName: `${subjectName}${countText}`,
                            items: agendas,
                        };
                    });
                    sortedList.push(...normalSubjectsWithCounts);

                    if (extra.length > 0) {
                        sortedList.push({ subjectName: 'EK GÜNDEMLER', items: [] });

                        const extraGroupedDecisions = groupDecisionsBySubject(extra);
                        const extraSubjectsWithCounts = extraGroupedDecisions.map(([subjectId, agendas]) => {
                            const subject = subjectList.find((sub) => sub.id === Number(subjectId));
                            const subjectName = subject ? subject.name : "Tanımsız Konu";
                            const { acceptCount, rejectCount, sameCount } = calculateCounts(agendas);
                            const counts = [];
                            if (acceptCount > 0) counts.push(`Kabul: ${acceptCount}`);
                            if (rejectCount > 0) counts.push(`Ret: ${rejectCount}`);
                            if (sameCount > 0) counts.push(`Eşit: ${sameCount}`);
                            const countText = counts.length > 0 ? ` (${counts.join(", ")})` : "";
                            return {
                                subjectId,
                                subjectName: `${subjectName}${countText}`,
                                items: agendas,
                            };
                        });
                        sortedList.push(...extraSubjectsWithCounts);
                    }

                    setGroupedList(sortedList);
                }
            } catch (error) {
                console.error("Error building preview decisions:", error);
            } finally {
                setReady(true);
            }
        };

        buildPreview();
    }, [meeting, meetingUsers, decisions, votes, reviews, decisionFiles, userList, subjectList]);

    async function processDecisions(voteList, members, decisionList) {
        const processedDecisions = await Promise.all(
            decisionList.map(async (decision) => {
                const votesForDecision = voteList.filter((vote) => vote.decisionId === decision.id);
                const result = await getVoteStatus(members, votesForDecision, userList, meeting);
                return {
                    ...decision,
                    voteResults: {
                        acceptCount: result.acceptCount,
                        rejectCount: result.rejectCount,
                        unvotedCount: result.unvotedCount,
                    },
                    voteResultType: result.voteResultType,
                    meetingUsers: result.processedUsers
                };
            })
        );
        return processedDecisions;
    }

    const scrollToUsersSection = () => {
        if (!usersSectionId) return;
        const section = document.getElementById(usersSectionId);
        if (section) {
            section.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <React.Fragment>
            {ready && (
                <JumboCardQuick
                    title={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{  }}>Toplantı Gündem ve Kararları</Box>
                            <Chip label={`${decisionCount} Gündem`} color="primary" size="small" />
                        </Box>
                    }
                    action={usersSectionId ? (
                        <Box
                            role="button"
                            tabIndex={0}
                            onClick={scrollToUsersSection}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    scrollToUsersSection();
                                }
                            }}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                cursor: "pointer",
                                color: "primary.main",
                            }}
                        >
                            <GroupsIcon sx={{  }} />
                            <Box sx={{  }}>Üyelere Git</Box>
                        </Box>
                    ) : null}
                    headerSx={{
                        borderBottom: 1,
                        borderBottomColor: "divider",
                        "& .MuiCardHeader-action": { alignSelf: "center", m: 0 },
                    }}
                    wrapperSx={{ p: 0 }}
                >
                    <Box sx={{ borderBottom: "1px solid #ccc", mb: 1 }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                                    <TableCell sx={{ width: "10%", textAlign: "left" }}>
                                        <Box sx={{ fontWeight: 500 }}>
                                            Karar No
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ width: "70%", textAlign: "left" }}>
                                        <Box sx={{ fontWeight: 500 }}>
                                            Gündem
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ width: "20%", textAlign: "left" }}>
                                        <Box sx={{ fontWeight: 500 }}>
                                            Oy Sonucu
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                        </Table>
                    </Box>

                    {groupedList.length === 0 ? (
                        <Box sx={{ py: 2, textAlign: "center" }}>
                            Gösterilecek karar bulunamadı.
                        </Box>
                    ) : meeting.inst === "YK" ? (
                        // YK: Departman bazında gruplama
                        groupedList.map((dept, deptIndex) => (
                            <Box key={deptIndex} sx={{
                                p: 2,
                                borderBottom: `1px solid ${primaryColor}`,
                                backgroundColor: deptIndex % 2 === 0 ? '#f8fafc' : '#ffffff'
                            }}>
                                <Box
                                    gutterBottom
                                    color="primary"
                                    sx={{
                                        color: "primary.main",
                                        textAlign: "center",
                                        fontWeight: 450,
                                        backgroundColor: '#eef2ff',
                                        py: 1,
                                        borderRadius: 1,
                                        mb: 2
                                    }}
                                >
                                    {dept.department}
                                </Box>
                                {dept.subjects.map((subject, subIndex) => (
                                    <Box key={subIndex} sx={{
                                        p: 1,
                                        borderBottom: "1px solid #e2e8f0",
                                        backgroundColor: subIndex % 2 === 0 ? '#ffffff' : '#f8fafc'
                                    }}>
                                        <Box
                                            sx={{
                                                fontWeight: 500,
                                                mb: 1,
                                                color: '#475569'
                                            }}
                                        >
                                            {subject.subjectName}
                                        </Box>
                                        {subject.items.map((agendaItem, itemIndex) => (
                                            <React.Fragment key={agendaItem.id}>
                                                <Box
                                                    sx={{
                                                        borderBottom: "1px solid #e2e8f0",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        backgroundColor: itemIndex % 2 === 0 ? '#ffffff' : '#f8fafc'
                                                    }}
                                                >
                                                    <Table>
                                                        <TableBody>
                                                            <TableRow sx={{
                                                                border: "0px solid #ffffff",
                                                                '&:hover': {
                                                                    backgroundColor: '#f1f5f9'
                                                                }
                                                            }}>
                                                                <TableCell sx={{
                                                                    width: "2%",
                                                                    backgroundColor: 'inherit'
                                                                }}>
                                                                    <Box
                                                                        sx={{
                                                                            mx: 1,
                                                                            fontWeight: 450,
                                                                            color: '#64748b'
                                                                        }}
                                                                    >
                                                                        {agendaItem.decisionNo}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell sx={{
                                                                    width: "83%",
                                                                    backgroundColor: 'inherit'
                                                                }}>
                                                                    <Box sx={{
                                                                        mx: 1,
                                                                        color: '#334155'
                                                                    }}>
                                                                        {agendaItem.agenda}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell sx={{
                                                                    width: "20%",
                                                                    backgroundColor: 'inherit'
                                                                }}>
                                                                    <Box sx={{
                                                                        mx: 1,
                                                                        color: '#334155'
                                                                    }}>
                                                                        <Box display="flex" alignItems="center">
                                                                            <ThumbUpIcon sx={{ color: greenColor, mr: 0.5 }} />
                                                                            <span style={{ marginRight: "10px" }}>
                                                                                {agendaItem.voteResults.acceptCount}
                                                                            </span>
                                                                            <Box mx={1}>|</Box>
                                                                            <ThumbDownIcon sx={{ color: redColor, mr: 0.5 }} />
                                                                            <span style={{ marginRight: "10px" }}>
                                                                                {agendaItem.voteResults.rejectCount}
                                                                            </span>
                                                                            <Box mx={1}>|</Box>
                                                                            <span>
                                                                                Kullanılmayan: {agendaItem.voteResults.unvotedCount}
                                                                            </span>
                                                                        </Box>
                                                                        <Box display="flex" alignItems="center">
                                                                            <span>
                                                                                {agendaItem.voteResultType ? agendaItem.voteResultType.text : ""}
                                                                            </span>
                                                                        </Box>
                                                                    </Box>
                                                                </TableCell>
                                                            </TableRow>
                                                            {(agendaItem.groupedData.length > 0 || agendaItem.groupedDataLink.length > 0) && (
                                                                <TableRow>
                                                                    <TableCell colSpan={3} sx={{ p: 0, border: 'none' }}>
                                                                        <Box sx={{
                                                                            '&:hover': {
                                                                                backgroundColor: '#f1f5f9'
                                                                            },
                                                                            width: "100%",
                                                                            py: 1,
                                                                            borderTop: '1px solid #e2e8f0',
                                                                            borderBottom: '1px solid #e2e8f0'
                                                                        }}>
                                                                            {agendaItem.groupedData.length > 0 ? (
                                                                                <>
                                                                                    <Box
                                                                                        sx={{
                                                                                            textAlign: "center",
                                                                                            mt: 1,
                                                                                            mb: 1,
                                                                                            fontWeight: 500,
                                                                                            color: '#475569'
                                                                                        }}
                                                                                    >
                                                                                        {agendaItem.decisionNo + " nolu gündeme ait dosyalar:"}
                                                                                    </Box>

                                                                                    {canViewSecretFiles(agendaItem, authUser) ? (
                                                                                        <Box sx={{ px: 7, mb: 1 }}>
                                                                                            <ReviewFileListTable
                                                                                                meeting={meeting}
                                                                                                grup={fileGroup}
                                                                                                groupedData={agendaItem.groupedData}
                                                                                                type={"preview"}
                                                                                                borderType={false}
                                                                                            />
                                                                                        </Box>
                                                                                    ) : (
                                                                                        <Box sx={{ px: 7, mb: 1, textAlign: "center", color: "error.main" }}>
                                                                                            Gizli dosyalar - Yetkiniz bulunmamaktadır
                                                                                        </Box>
                                                                                    )}
                                                                                </>
                                                                            ) : null}

                                                                            {agendaItem.groupedDataLink.length > 0 ? (
                                                                                <>
                                                                                    <Box
                                                                                        sx={{
                                                                                            textAlign: "center",
                                                                                            mt: 1,
                                                                                            mb: 1,
                                                                                            fontWeight: 500,
                                                                                            color: '#475569'
                                                                                        }}
                                                                                    >
                                                                                        {agendaItem.decisionNo + " nolu gündeme ait bağlantılar:"}
                                                                                    </Box>

                                                                                    {canViewSecretFiles(agendaItem, authUser) ? (
                                                                                        <Box sx={{ px: 7, mb: 1 }}>
                                                                                            <ReviewFileListTable
                                                                                                meeting={meeting}
                                                                                                grup={fileGroup}
                                                                                                groupedData={agendaItem.groupedDataLink}
                                                                                                type={"preview"}
                                                                                                dataType={"link"}
                                                                                                borderType={false}
                                                                                            />
                                                                                        </Box>
                                                                                    ) : (
                                                                                        <Box sx={{ px: 7, mb: 1, textAlign: "center", color: "error.main" }}>
                                                                                            Gizli bağlantılar - Yetkiniz bulunmamaktadır
                                                                                        </Box>
                                                                                    )}
                                                                                </>
                                                                            ) : null}
                                                                        </Box>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}

                                                            {/* Review Section */}
                                                            {agendaItem.reviews && agendaItem.reviews.length > 0 && (
                                                                <TableRow>
                                                                    <TableCell colSpan={3} sx={{ p: 0, border: 'none' }}>
                                                                        <Box sx={{
                                                                            '&:hover': {
                                                                                backgroundColor: '#f1f5f9'
                                                                            },
                                                                            width: "100%",
                                                                            py: 1,
                                                                            borderTop: '1px solid #e2e8f0',
                                                                            borderBottom: '1px solid #e2e8f0',
                                                                            backgroundColor: '#e3f2fd'
                                                                        }}>
                                                                            <Box
                                                                                sx={{
                                                                                    textAlign: "center",
                                                                                    mt: 1,
                                                                                    mb: 1,
                                                                                    fontWeight: 500,
                                                                                    color: '#475569'
                                                                                }}
                                                                            >
                                                                                {agendaItem.decisionNo + " nolu gündeme ait değerlendirmeler:"}
                                                                            </Box>
                                                                            <Box sx={{ px: 7, mb: 1 }}>
                                                                                {agendaItem.reviews.map((review, reviewIndex) => {
                                                                                    const user = meetingUsers.find(item => item.userId === review.userId);
                                                                                    const userInfo = userList.find(item => item.id === review.userId);

                                                                                    const memberRoleText = user && user.acting === "Vekaleten" ? " (V.)" : "";
                                                                                    const memberRoleDisplay = user?.memberRole ? `${user.memberRole}${memberRoleText}` : "";

                                                                                    return (
                                                                                        <Box key={review.id} sx={{
                                                                                            mb: reviewIndex < agendaItem.reviews.length - 1 ? 1 : 0,
                                                                                            color: '#424242',
                                                                                            lineHeight: 1.5
                                                                                        }}>
                                                                                            <span >{getUserFullName(userInfo)}</span> <span >({user?.inst || 'Bilinmiyor'})</span> <span >{memberRoleDisplay}</span> : <span style={{ fontStyle: 'italic' }}>{review.info}</span> <span style={{ fontStyle: 'italic' }}>({getLocaleDateTime(review.pdate)})</span>
                                                                                        </Box>
                                                                                    );
                                                                                })}
                                                                            </Box>
                                                                        </Box>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </Box>
                                            </React.Fragment>
                                        ))}
                                    </Box>
                                ))}
                            </Box>
                        ))
                    ) : (
                        // meeting.inst "YK" değilse: mevcut subject bazlı gruplama
                        groupedList.map((subject, index) => (
                            <Box key={index} sx={{
                                p: 2,
                                borderBottom: `1px solid ${primaryColor}`,
                                backgroundColor: index % 2 === 0 ? '#f8fafc' : '#ffffff'
                            }}>
                                <Box
                                    gutterBottom
                                    color="primary"
                                    sx={{
                                        color: "primary.main",
                                        textAlign: "center",
                                        fontWeight: 450,
                                        backgroundColor: '#eef2ff',
                                        py: 1,
                                        borderRadius: 1,
                                        mb: 2
                                    }}
                                >
                                    {subject.subjectName}
                                </Box>
                                {subject.items.map((agendaItem, itemIndex) => (
                                    <React.Fragment key={agendaItem.id}>
                                        <Box sx={{
                                            borderBottom: "1px solid #e2e8f0",
                                            display: "flex",
                                            flexDirection: "column",
                                            backgroundColor: itemIndex % 2 === 0 ? '#ffffff' : '#f8fafc'
                                        }}>
                                            <Table>
                                                <TableBody>
                                                    <TableRow sx={{
                                                        '&:hover': {
                                                            backgroundColor: '#f1f5f9'
                                                        }
                                                    }}>
                                                        <TableCell sx={{
                                                            width: "2%",
                                                            backgroundColor: 'inherit'
                                                        }}>
                                                            <Box sx={{
                                                                mx: 1,
                                                                fontWeight: 450,
                                                                color: '#64748b'
                                                            }}>
                                                                {agendaItem.decisionNo}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            width: "83%",
                                                            backgroundColor: 'inherit'
                                                        }}>
                                                            <Box sx={{
                                                                mx: 1,
                                                                color: '#334155'
                                                            }}>
                                                                {agendaItem.agenda}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            width: "20%",
                                                            backgroundColor: 'inherit'
                                                        }}>
                                                            <Box sx={{
                                                                mx: 1,
                                                                color: '#334155'
                                                            }}>
                                                                <Box display="flex" alignItems="center">
                                                                    <ThumbUpIcon sx={{ color: greenColor, mr: 0.5 }} />
                                                                    <span style={{ marginRight: "10px" }}>
                                                                        {agendaItem.voteResults.acceptCount}
                                                                    </span>
                                                                    <Box mx={1}>|</Box>
                                                                    <ThumbDownIcon sx={{ color: redColor, mr: 0.5 }} />
                                                                    <span style={{ marginRight: "10px" }}>
                                                                        {agendaItem.voteResults.rejectCount}
                                                                    </span>
                                                                    <Box mx={1}>|</Box>
                                                                    <span>
                                                                        Kullanılmayan: {agendaItem.voteResults.unvotedCount}
                                                                    </span>
                                                                </Box>
                                                                <Box display="flex" alignItems="center">
                                                                    <span>
                                                                        {agendaItem.voteResultType ? agendaItem.voteResultType.text : ""}
                                                                    </span>
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                    {(agendaItem.groupedData.length > 0 || agendaItem.groupedDataLink.length > 0) && (
                                                        <TableRow>
                                                            <TableCell colSpan={3} sx={{ p: 0, border: 'none' }}>
                                                                <Box sx={{
                                                                    '&:hover': {
                                                                        backgroundColor: '#f1f5f9'
                                                                    },
                                                                    width: "100%",
                                                                    py: 1,
                                                                    borderTop: '1px solid #e2e8f0',
                                                                    borderBottom: '1px solid #e2e8f0'
                                                                }}>
                                                                    {agendaItem.groupedData.length > 0 ? (
                                                                        <>
                                                                            <Box
                                                                                sx={{
                                                                                    textAlign: "center",
                                                                                    mt: 1,
                                                                                    mb: 1,
                                                                                    fontWeight: 500,
                                                                                    color: '#475569'
                                                                                }}
                                                                            >
                                                                                {agendaItem.decisionNo + " nolu gündeme ait dosyalar:"}
                                                                            </Box>

                                                                            {canViewSecretFiles(agendaItem, authUser) ? (
                                                                                <Box sx={{ px: 7, mb: 1 }}>
                                                                                    <ReviewFileListTable
                                                                                        meeting={meeting}
                                                                                        grup={fileGroup}
                                                                                        groupedData={agendaItem.groupedData}
                                                                                        type={"preview"}
                                                                                        borderType={false}
                                                                                    />
                                                                                </Box>
                                                                            ) : (
                                                                                <Box sx={{ px: 7, mb: 1, textAlign: "center", color: "error.main" }}>
                                                                                    Gizli dosyalar - Yetkiniz bulunmamaktadır
                                                                                </Box>
                                                                            )}
                                                                        </>
                                                                    ) : null}

                                                                    {agendaItem.groupedDataLink.length > 0 ? (
                                                                        <>
                                                                            <Box
                                                                                sx={{
                                                                                    textAlign: "center",
                                                                                    mt: 1,
                                                                                    mb: 1,
                                                                                    fontWeight: 500,
                                                                                    color: '#475569'
                                                                                }}
                                                                            >
                                                                                {agendaItem.decisionNo + " nolu gündeme ait bağlantılar:"}
                                                                            </Box>

                                                                            {canViewSecretFiles(agendaItem, authUser) ? (
                                                                                <Box sx={{ px: 7, mb: 1 }}>
                                                                                    <ReviewFileListTable
                                                                                        meeting={meeting}
                                                                                        grup={fileGroup}
                                                                                        groupedData={agendaItem.groupedDataLink}
                                                                                        type={"preview"}
                                                                                        dataType={"link"}
                                                                                        borderType={false}
                                                                                    />
                                                                                </Box>
                                                                            ) : (
                                                                                <Box sx={{ px: 7, mb: 1, textAlign: "center", color: "error.main" }}>
                                                                                    Gizli bağlantılar - Yetkiniz bulunmamaktadır
                                                                                </Box>
                                                                            )}
                                                                        </>
                                                                    ) : null}
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}

                                                    {/* Review Section */}
                                                    {agendaItem.reviews && agendaItem.reviews.length > 0 && (
                                                        <TableRow>
                                                            <TableCell colSpan={3} sx={{ p: 0, border: 'none' }}>
                                                                <Box sx={{
                                                                    '&:hover': {
                                                                        backgroundColor: '#f1f5f9'
                                                                    },
                                                                    width: "100%",
                                                                    py: 1,
                                                                    borderTop: '1px solid #e2e8f0',
                                                                    borderBottom: '1px solid #e2e8f0',
                                                                    backgroundColor: '#e3f2fd'

                                                                }}>
                                                                    <Box
                                                                        sx={{
                                                                            textAlign: "center",
                                                                            mt: 1,
                                                                            mb: 1,
                                                                            fontWeight: 500,
                                                                            color: '#475569'
                                                                        }}
                                                                    >
                                                                        {agendaItem.decisionNo + " nolu gündeme ait değerlendirmeler:"}
                                                                    </Box>
                                                                    <Box sx={{ px: 7, mb: 1 }}>
                                                                        {agendaItem.reviews.map((review, reviewIndex) => {
                                                                            const user = meetingUsers.find(item => item.userId === review.userId);
                                                                            const userInfo = userList.find(item => item.id === review.userId);

                                                                            const memberRoleText = user && user.acting === "Vekaleten" ? " V." : "";
                                                                            const memberRoleDisplay = user?.memberRole ? `(${user.memberRole}${memberRoleText})` : "";

                                                                            return (
                                                                                <Box key={review.id} sx={{
                                                                                    mb: reviewIndex < agendaItem.reviews.length - 1 ? 1 : 0,
                                                                                    color: '#424242',
                                                                                    lineHeight: 1
                                                                                }}>
                                                                                    <span >{getUserFullName(userInfo)}</span> <span >({user?.inst || 'Bilinmiyor'})</span> <span >{memberRoleDisplay}</span> : <span style={{ fontStyle: 'italic' }}>{review.info}</span> <span style={{ fontStyle: 'italic' }}>({getLocaleDateTime(review.pdate)})</span>
                                                                                </Box>
                                                                            );
                                                                        })}
                                                                    </Box>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    </React.Fragment>
                                ))}
                            </Box>
                        ))
                    )}
                </JumboCardQuick>
            )}
        </React.Fragment>
    );
}

export default PreviewMeetingCard;
